/**
 * The registration form of one action as the editor holds it, and the request it sends (D23).
 *
 * The server is the one judge: `validated_lead_fields` checks the whole list and answers with
 * every problem in one German list. This module knows only what the editor shows before it
 * asks: which switches a field offers, what a field nobody configured starts as, and when a
 * hint belongs on the page.
 */

import { type CompletionFormField, type FormBlock, formBlockOrder } from "@countdown/theme";

import type { ActionDetail, ConsentType, LeadFieldBody, LeadFieldsRequest } from "@/lib/api";

type LeadField = ActionDetail["lead_fields"][number];

/** How a field appears on the form (`FIELD_MODES` on the server), in the editor's order. */
export type Mode = "hidden" | "optional" | "required";

/** How the form asks each consent (D51), in the words of the field modes. */
export type ConsentModes = Readonly<Record<ConsentType, Mode>>;

/** An action from before D51 whose detail carries no modes: as the server reads it then. */
const FALLBACK_MODES: ConsentModes = {
  registration: "required",
  newsletter: "optional",
  sponsor_contact: "optional",
  daily_reminder: "optional",
};

export const MODE_LABELS: Readonly<Record<Mode, string>> = {
  hidden: "Aus",
  optional: "Optional",
  required: "Pflicht",
};

/** The inputs a field of the action's own may have (`CUSTOM_TYPES` on the server). */
export const TYPE_LABELS = {
  text: "Text",
  textarea: "Textfeld (mehrzeilig)",
  select: "Auswahl",
  checkbox: "Kästchen",
  number: "Zahl",
} as const;

export type FieldType = keyof typeof TYPE_LABELS;

/** One switchable field: one the platform knows, or one of the action's own. */
export interface FieldRow {
  /**
   * The field's key. A field of the action's own that was never saved holds a stand-in
   * (`neu-1`) until the answer to the save brings the key the server gave it. A server key
   * never contains a hyphen, so the two cannot meet.
   */
  readonly key: string;
  readonly label: string;
  readonly mode: Mode;
  readonly transmit: boolean;
  /** `null` for a field the platform knows. */
  readonly type: FieldType | null;
  /** The choices of an Auswahl as typed, one per line. */
  readonly options: string;
}

export interface RegistrationForm {
  /** Page title and heading of the completion page (D35); empty gives the page's own. */
  readonly title: string;
  readonly headline: string;
  readonly intro: string;
  /** The submit button (D35); empty gives the page's own. */
  readonly submitLabel: string;
  /** The option texts (D37, form part); empty gives the page's own words. */
  readonly emailLabel: string;
  readonly emailHint: string;
  readonly termsLabel: string;
  readonly accountTitle: string;
  readonly accountHint: string;
  /** The blocks of the form in reading order, always complete. */
  readonly blockOrder: readonly FormBlock[];
  /** Always asked, always required and transmitted; it travels back with its stored label. */
  readonly email: LeadField;
  /**
   * Every field the platform knows and the action's own, in the order participants read them:
   * the stored order, which the server renders as it is. A platform field never configured
   * follows the stored ones, in the platform's order.
   */
  readonly fields: readonly FieldRow[];
  /**
   * Fields set up before the form existed, such as the name an account supplies or a postal
   * address asked from winners only. Never on the form; they travel back as stored.
   */
  readonly kept: readonly LeadField[];
  /** No field of the platform was configured, so first and last name stand as proposed. */
  readonly proposed: boolean;
  /** How the form asks each consent (D51). The address and the terms stay mandatory. */
  readonly consentModes: ConsentModes;
}

const EMAIL = "email";
const NEW_KEY = "neu-";

/**
 * What an action without a configured field of the platform starts with: first and last name
 * required and transmitted (open point 2, decided 30.09.2026). Everything else is off.
 */
const PROPOSED: Readonly<Record<string, Mode>> = {
  first_name: "required",
  last_name: "required",
};

const ALL_MODES: readonly Mode[] = ["hidden", "optional", "required"];
/** A box that must be ticked is a consent, and consents are evidenced on their own. */
const BOX_MODES: readonly Mode[] = ["hidden", "optional"];

const ADDRESS_KEYS: ReadonlySet<string> = new Set([
  "street",
  "address_supplement",
  "postal_code",
  "city",
]);
const FREE_TEXT: ReadonlySet<FieldType | null> = new Set(["text", "textarea"]);

/** The switches a field offers. */
export function modesFor(type: FieldType | null): readonly Mode[] {
  return type === "checkbox" ? BOX_MODES : ALL_MODES;
}

/** The row with another input type; a mode the new type does not offer becomes optional. */
export function withType(row: FieldRow, type: FieldType): FieldRow {
  return { ...row, type, mode: modesFor(type).includes(row.mode) ? row.mode : "optional" };
}

/** A new field of the action's own, numbered by the editor until the server gives it a key. */
export function newField(count: number): FieldRow {
  return {
    key: `${NEW_KEY}${String(count)}`,
    label: "",
    mode: "optional",
    transmit: true,
    type: "text",
    options: "",
  };
}

function modeOf(value: string): Mode {
  return value === "hidden" || value === "required" ? value : "optional";
}

function typeOf(value: string | null): FieldType {
  return value !== null && value in TYPE_LABELS ? (value as FieldType) : "text";
}

/**
 * The form as the server stores it, or as proposed where nothing is stored yet. A form that
 * cannot be saved passes `propose = false`: it shows what participants get, nothing else.
 */
export function formOf(
  detail: Pick<
    ActionDetail,
    "lead_fields" | "core_lead_fields" | "lead_form_headline" | "lead_form_intro"
  > &
    Partial<
      Pick<
        ActionDetail,
        | "consent_modes"
        | "lead_form_title"
        | "lead_form_submit_label"
        | "lead_form_email_label"
        | "lead_form_email_hint"
        | "lead_form_terms_label"
        | "lead_form_account_title"
        | "lead_form_account_hint"
        | "lead_form_block_order"
      >
    >,
  propose = true,
): RegistrationForm {
  const stored = new Map(detail.lead_fields.map((field) => [field.key, field]));
  const coreKeys = new Set(detail.core_lead_fields.map((field) => field.key));
  const proposed = propose && !detail.lead_fields.some((field) => coreKeys.has(field.key));
  return {
    title: detail.lead_form_title ?? "",
    headline: detail.lead_form_headline ?? "",
    intro: detail.lead_form_intro ?? "",
    submitLabel: detail.lead_form_submit_label ?? "",
    emailLabel: detail.lead_form_email_label ?? "",
    emailHint: detail.lead_form_email_hint ?? "",
    termsLabel: detail.lead_form_terms_label ?? "",
    accountTitle: detail.lead_form_account_title ?? "",
    accountHint: detail.lead_form_account_hint ?? "",
    blockOrder: formBlockOrder(detail.lead_form_block_order),
    consentModes: { ...FALLBACK_MODES, ...detail.consent_modes },
    email: stored.get(EMAIL) ?? {
      key: EMAIL,
      label: "E-Mail-Adresse",
      collect_at: "registration",
      transmit: true,
      mode: "required",
      type: null,
      options: [],
    },
    fields: [
      ...detail.lead_fields
        .filter((field) => coreKeys.has(field.key) || field.key.startsWith("f_"))
        .map((field) => {
          const row: FieldRow = {
            key: field.key,
            label: field.label,
            mode: modeOf(field.mode),
            transmit: field.transmit,
            type: null,
            options: "",
          };
          return coreKeys.has(field.key)
            ? row
            : withType({ ...row, options: field.options.join("\n") }, typeOf(field.type));
        }),
      ...detail.core_lead_fields
        .filter((field) => !stored.has(field.key))
        .map((field) => ({
          key: field.key,
          label: field.label,
          mode: proposed ? (PROPOSED[field.key] ?? "hidden") : "hidden",
          transmit: true,
          type: null,
          options: "",
        })),
    ],
    kept: detail.lead_fields.filter(
      (field) =>
        field.key !== EMAIL && !coreKeys.has(field.key) && !field.key.startsWith("f_"),
    ),
    proposed,
  };
}

/** The choices of an Auswahl: one per line, blank lines left out. */
export function optionList(text: string): string[] {
  return text
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line !== "");
}

function fieldBody(row: FieldRow): LeadFieldBody {
  return {
    key: row.key.startsWith(NEW_KEY) ? "" : row.key,
    label: row.label.trim(),
    collect_at: "registration",
    transmit: row.transmit,
    mode: row.mode,
    ...(row.type === null ? {} : { type: row.type }),
    ...(row.type === "select" ? { options: optionList(row.options) } : {}),
  };
}

/**
 * The whole form in the order the editor shows it: the address, the fields, then whatever an
 * earlier setup left. Every text and the block order always
 * travel; an empty text removes the stored one, and the page shows its own.
 */
export function requestOf(form: RegistrationForm): LeadFieldsRequest {
  return {
    title: form.title.trim(),
    headline: form.headline.trim(),
    intro: form.intro.trim(),
    submit_label: form.submitLabel.trim(),
    email_label: form.emailLabel.trim(),
    email_hint: form.emailHint.trim(),
    terms_label: form.termsLabel.trim(),
    account_title: form.accountTitle.trim(),
    account_hint: form.accountHint.trim(),
    block_order: [...form.blockOrder],
    consents: { ...form.consentModes },
    fields: [
      {
        key: EMAIL,
        label: form.email.label,
        collect_at: "registration",
        transmit: true,
        mode: "required",
      },
      ...form.fields.map(fieldBody),
      ...form.kept.map((field) => ({
        key: field.key,
        label: field.label,
        collect_at: field.collect_at ?? "registration",
        transmit: field.transmit,
        mode: field.mode,
      })),
    ],
  };
}

const asked = (row: FieldRow): boolean => row.mode !== "hidden";

/**
 * The inputs the completion page would render from this form, in its order (`form_fields` on
 * the server), a platform field with its own input type. A box is never required. The length
 * limit does not show, so one value stands for all.
 */
export function previewFields(
  form: RegistrationForm,
  core: ActionDetail["core_lead_fields"],
): CompletionFormField[] {
  const known = new Map(core.map((field) => [field.key, field]));
  return form.fields.filter(asked).map((row) => ({
    key: row.key,
    label: row.label,
    type: row.type ?? known.get(row.key)?.type ?? "text",
    required: row.mode === "required" && row.type !== "checkbox",
    max_length: 200,
    options: row.type === null ? (known.get(row.key)?.options ?? []) : optionList(row.options),
  }));
}

/** The field is a part of the postal address and asked (D23: off unless a sponsor needs it). */
export function asksForAddress(row: FieldRow): boolean {
  return ADDRESS_KEYS.has(row.key) && asked(row);
}

/** The field is a free text of the action's own and asked; it can draw health details (Art. 9). */
export function asksForFreeText(row: FieldRow): boolean {
  return FREE_TEXT.has(row.type) && asked(row);
}

/**
 * The order with `entry` put where `target` stands; the entries between move by one, as a
 * dragged row does. Moving up or down past the next listed entry is the same call with that
 * entry as the target, so one the editor does not list (the account offer without an identity
 * service, a switched-off field) is never a step of its own. For blocks and for field keys.
 */
export function movedBlock<T extends string>(order: readonly T[], entry: T, target: T): T[] {
  const from = order.indexOf(entry);
  const to = order.indexOf(target);
  if (from < 0 || to < 0 || from === to) {
    return [...order];
  }
  const next = order.filter((item) => item !== entry);
  next.splice(to, 0, entry);
  return next;
}
